import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Phone, Menu, X, ChevronDown, LogOut, User as UserIcon } from "lucide-react";

const langs = ["ua", "ru", "en"];

export default function Header({ onAuth, onConsult }) {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const navItems = [
    { href: "#services", label: t.nav.services },
    { href: "#gallery", label: t.nav.gallery },
    { href: "#about", label: t.nav.about },
    { href: "#faq", label: t.nav.faq },
    { href: "#contact", label: t.nav.contact },
  ];

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-700 ease-editorial ${
          scrolled ? "bg-onyx/90 backdrop-blur-xl border-b border-bone/5" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          {/* Brand */}
          <a href="#" className="flex items-center gap-3 group" data-testid="brand-logo">
            <span className="font-display italic-display text-3xl text-brass leading-none">Д</span>
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-display text-lg tracking-tight text-bone">Джавала</span>
              <span className="eyebrow text-[0.6rem] tracking-[0.32em]">Marmor · Україна</span>
            </div>
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {navItems.map((n) => (
              <a
                key={n.href}
                href={n.href}
                data-testid={`nav-${n.href.replace("#","")}`}
                className="text-[0.78rem] tracking-[0.18em] uppercase text-bone/75 hover:text-brass transition-colors duration-500 link-under"
              >
                {n.label}
              </a>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-4">
            {/* Language */}
            <div className="hidden md:flex items-center gap-1 text-[0.7rem] tracking-[0.2em] uppercase">
              {langs.map((l, i) => (
                <React.Fragment key={l}>
                  <button
                    data-testid={`lang-${l}`}
                    onClick={() => setLang(l)}
                    className={`px-1 transition-colors ${lang === l ? "text-brass" : "text-bone/50 hover:text-bone"}`}
                  >
                    {l.toUpperCase()}
                  </button>
                  {i < langs.length - 1 && <span className="text-bone/20">·</span>}
                </React.Fragment>
              ))}
            </div>

            <a
              href="tel:+380957955234"
              data-testid="header-call-cta"
              className="hidden md:inline-flex items-center gap-2 text-[0.78rem] tracking-[0.18em] uppercase text-bone hover:text-brass transition-colors"
            >
              <Phone className="w-3.5 h-3.5" /> +38 (095) 795-52-34
            </a>

            {/* Auth */}
            {user ? (
              <div className="relative" ref={menuRef}>
                <button
                  data-testid="user-menu-trigger"
                  onClick={() => setMenu((v) => !v)}
                  className="flex items-center gap-2 text-bone hover:text-brass transition-colors"
                >
                  <span className="w-8 h-8 rounded-full bg-graphite border border-bone/10 grid place-items-center overflow-hidden">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="font-display text-sm text-brass">{(user.name || user.email)[0].toUpperCase()}</span>
                    )}
                  </span>
                  <ChevronDown className="w-4 h-4 hidden md:block" />
                </button>
                {menu && (
                  <div className="absolute right-0 mt-3 w-56 glass rounded-sm py-2" data-testid="user-menu">
                    <div className="px-4 py-2 border-b border-bone/5">
                      <div className="text-sm text-bone truncate">{user.name || user.email}</div>
                      <div className="text-xs text-taupe truncate">{user.email}</div>
                    </div>
                    <a href="/dashboard" data-testid="menu-dashboard" className="flex items-center gap-2 px-4 py-2 text-sm text-bone/80 hover:text-brass hover:bg-bone/5 transition">
                      <UserIcon className="w-4 h-4" /> {t.cta.dashboard}
                    </a>
                    {user.role === "admin" && (
                      <a href="/admin" data-testid="menu-admin" className="flex items-center gap-2 px-4 py-2 text-sm text-bone/80 hover:text-brass hover:bg-bone/5 transition">
                        <UserIcon className="w-4 h-4" /> {t.cta.admin}
                      </a>
                    )}
                    <button data-testid="menu-logout" onClick={logout} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-bone/80 hover:text-brass hover:bg-bone/5 transition">
                      <LogOut className="w-4 h-4" /> {t.cta.logout}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                data-testid="header-auth-btn"
                onClick={onAuth}
                className="hidden sm:inline-flex text-[0.78rem] tracking-[0.18em] uppercase text-bone hover:text-brass transition-colors"
              >
                {t.cta.login}
              </button>
            )}

            <button
              data-testid="header-consult-btn"
              onClick={onConsult}
              className="hidden md:inline-flex btn-brass !py-3 !px-5"
            >
              {t.nav.consult}
            </button>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              className="lg:hidden text-bone p-2"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden transition-opacity duration-500 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="mobile-drawer"
      >
        <div className="absolute inset-0 bg-onyx/90 backdrop-blur-xl" onClick={() => setOpen(false)} />
        <div className={`absolute right-0 top-0 h-full w-[85%] max-w-sm bg-charcoal border-l border-bone/10 p-8 transition-transform duration-700 ease-editorial ${open ? "translate-x-0" : "translate-x-full"}`}>
          <div className="flex items-center justify-between mb-12">
            <span className="font-display italic-display text-3xl text-brass">Д</span>
            <button onClick={() => setOpen(false)} className="text-bone p-2"><X className="w-6 h-6" /></button>
          </div>
          <nav className="flex flex-col gap-6">
            {navItems.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="font-display text-3xl text-bone hover:text-brass transition-colors"
              >
                {n.label}
              </a>
            ))}
          </nav>
          <div className="mt-12 pt-8 border-t border-bone/10 space-y-4">
            <a href="tel:+380957955234" className="block text-bone link-under text-sm tracking-[0.18em] uppercase">+38 (095) 795-52-34</a>
            <button onClick={() => { setOpen(false); onConsult(); }} className="btn-brass w-full justify-center">{t.nav.consult}</button>
            {!user && (
              <button onClick={() => { setOpen(false); onAuth(); }} className="btn-ghost w-full justify-center">{t.cta.login}</button>
            )}
            <div className="flex items-center justify-center gap-3 pt-4">
              {langs.map((l) => (
                <button key={l} onClick={() => setLang(l)} className={`text-xs tracking-[0.2em] uppercase ${lang === l ? "text-brass" : "text-bone/50"}`}>{l.toUpperCase()}</button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
